/**
 * Volet « planning » des Réglages (specs §6.8, §6.1 ; archi §4.4).
 *
 * ⚠️ Arbitrage Bibliothèque / Réglages : les *modèles* se créent et s'éditent
 * dans la Bibliothèque, leur *affectation aux jours* se règle ici. Le
 * raisonnement complet est en tête de `screens/library/LibraryScreen.tsx`.
 *
 * ⚠️ **La surcharge prime toujours sur la récurrence** (§4.4), et ne la casse
 * pas : la ligne hebdomadaire reste en place, elle est seulement ignorée ce
 * jour-là. L'écran le dit, parce que c'est exactement la promesse du §6.1
 * (« surcharger ponctuellement une date précise sans casser la récurrence »).
 *
 * ⚠️ Une surcharge ne joue qu'à la **création** de la journée (§4.5). Si la
 * journée existe déjà, son snapshot est pris : il faut alors passer par
 * « appliquer un modèle » depuis l'écran Aujourd'hui. C'est écrit à l'écran,
 * sinon le réglage paraîtrait sans effet.
 */

import { readError } from '../../api/client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import {
  clearOverride,
  clearWeekly,
  fetchPlanning,
  fetchTemplates,
  setOverride,
  setWeekly,
} from '../../api/templates';
import { Button, ErrorNote, Hint } from '../../components/ui/primitives';
import { ReadNote } from '../../components/ui/ReadNote';
import { TOKENS } from '../../components/ui/tokens';
import { browserToday, formatDayLabel, WEEKDAY_LABELS } from '../../lib/dates';

/**
 * ⚠️ **Ces `<select>` étaient les derniers contrôles natifs non stylés de
 * l'application** (audit de l'étape 10) : fond du système, flèche du système,
 * hauteur du système. Ils portent désormais `ui-select`, qui retire l'apparence
 * native et redessine le chevron — sans quoi `appearance: none` laisserait un
 * champ qui ne dit plus qu'il s'ouvre.
 *
 * ⚠️ **`fontSize` remonte de 0,9 rem à 1 rem**, imposé par `controls.css` : sous
 * 16 px, Safari iOS zoome à la mise au point. Le défaut ne se voyait pas ici
 * parce qu'un `<select>` iOS ouvre une roue plutôt qu'un clavier — mais le
 * champ `date` voisin, lui, était bien concerné.
 *
 * Ce qui reste en ligne est de la **largeur**, pas de l'apparence : elle dépend
 * de la place dont chaque contrôle dispose dans sa rangée.
 */
const SELECT_WIDTH = { maxWidth: '13rem' } as const;

export function PlanningSection() {
  const queryClient = useQueryClient();
  const [overrideDate, setOverrideDate] = useState(browserToday);
  const [overrideTemplate, setOverrideTemplate] = useState('');

  const templates = useQuery({ queryKey: ['templates'], queryFn: fetchTemplates });
  const planning = useQuery({ queryKey: ['planning'], queryFn: fetchPlanning });

  const onPlanningWritten = (fresh: Awaited<ReturnType<typeof fetchPlanning>>) => {
    queryClient.setQueryData(['planning'], fresh);
    // Le planning ne décide que des journées **à créer** : celles déjà en cache
    // ne changent pas, mais une date encore jamais ouverte doit être relue.
    void queryClient.invalidateQueries({ queryKey: ['day'] });
  };

  const weekly = useMutation({
    mutationFn: ({ weekday, templateId }: { weekday: number; templateId: number | null }) =>
      templateId === null ? clearWeekly(weekday) : setWeekly(weekday, templateId),
    onSuccess: onPlanningWritten,
  });

  const override = useMutation({
    mutationFn: ({ date, templateId }: { date: string; templateId: number | null }) =>
      templateId === null ? clearOverride(date) : setOverride(date, templateId),
    onSuccess: onPlanningWritten,
  });

  const nameOf = (templateId: number): string =>
    templates.data?.find((template) => template.id === templateId)?.name ??
    `Modèle ${String(templateId)}`;

  const noTemplates = templates.data !== undefined && templates.data.length === 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <h2 style={{ fontSize: '1rem', margin: 0 }}>Planning</h2>

      <ReadNote error={readError(planning)} quoi="le planning" />
      {weekly.isError && <ErrorNote>{weekly.error.message}</ErrorNote>}
      {override.isError && <ErrorNote>{override.error.message}</ErrorNote>}

      {noTemplates ? (
        <Hint>
          Aucun modèle de journée. En créer un dans l’onglet Bibliothèque : le planning ne fait
          qu’associer un modèle existant à un jour.
        </Hint>
      ) : (
        <>
          {/* --- Récurrence hebdomadaire ---------------------------------- */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
            <Hint>Récurrence hebdomadaire — s’applique à toutes les semaines.</Hint>
            {WEEKDAY_LABELS.map((label, weekday) => (
              <label
                key={label}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '0.5rem',
                }}
              >
                <span style={{ fontSize: '0.9rem' }}>{label}</span>
                <select
                  value={planning.data?.weekly[weekday] ?? ''}
                  disabled={weekly.isPending || planning.isPending}
                  onChange={(event) => {
                    weekly.mutate({
                      weekday,
                      templateId: event.target.value === '' ? null : Number(event.target.value),
                    });
                  }}
                  className="ui-select"
                  style={SELECT_WIDTH}
                >
                  <option value="">— aucun —</option>
                  {templates.data?.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
                </select>
              </label>
            ))}
          </div>

          {/* --- Surcharges ponctuelles ----------------------------------- */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <Hint>
              Surcharge ponctuelle — prime sur la récurrence pour cette date seulement, sans la
              casser.
            </Hint>

            {planning.data && planning.data.overrides.length > 0 && (
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {planning.data.overrides.map((entry) => (
                  <li
                    key={entry.date}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '0.5rem',
                      padding: '0.4rem 0',
                      borderBottom: `1px solid ${TOKENS.border}`,
                    }}
                  >
                    <span style={{ fontSize: '0.85rem' }}>
                      {formatDayLabel(entry.date)}{' '}
                      <span style={{ color: TOKENS.textQuiet }}>→ {nameOf(entry.templateId)}</span>
                    </span>
                    <Button
                      variant="quiet"
                      ariaLabel={`Retirer la surcharge du ${entry.date}`}
                      disabled={override.isPending}
                      onClick={() => {
                        override.mutate({ date: entry.date, templateId: null });
                      }}
                      style={{ padding: '0 0.6rem', fontSize: '0.8rem' }}
                    >
                      ✕
                    </Button>
                  </li>
                ))}
              </ul>
            )}

            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center' }}>
              <input
                type="date"
                value={overrideDate}
                aria-label="Date à surcharger"
                onChange={(event) => {
                  setOverrideDate(event.target.value);
                }}
                className="ui-input"
                style={{ maxWidth: '10rem' }}
              />
              <select
                value={overrideTemplate}
                aria-label="Modèle à appliquer"
                onChange={(event) => {
                  setOverrideTemplate(event.target.value);
                }}
                className="ui-select"
                style={SELECT_WIDTH}
              >
                <option value="">— choisir —</option>
                {templates.data?.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.name}
                  </option>
                ))}
              </select>
              <Button
                variant="quiet"
                disabled={overrideDate === '' || overrideTemplate === '' || override.isPending}
                onClick={() => {
                  override.mutate({
                    date: overrideDate,
                    templateId: Number(overrideTemplate),
                  });
                  setOverrideTemplate('');
                }}
              >
                Surcharger
              </Button>
            </div>

            <Hint>
              Une surcharge s’applique au moment où la journée est créée. Si la journée a déjà été
              ouverte, passer par « appliquer un modèle » depuis l’écran Aujourd’hui.
            </Hint>
          </div>
        </>
      )}
    </div>
  );
}
